import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { ArrowLeft, Sparkles, Target, Clock, CheckCircle2, AlertCircle, BookOpen } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PDI180 = () => {
  const navigate = useNavigate();
  const { cicloId } = useParams();
  
  const [loading, setLoading] = useState(false);
  const [pdi, setPdi] = useState(null);
  const [ciclo, setCiclo] = useState(null);

  useEffect(() => {
    fetchPDI();
    fetchCiclo();
  }, [cicloId]);

  const fetchCiclo = async () => {
    try {
      const response = await axios.get(`${API}/api/feedback-180/ciclos/${cicloId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setCiclo(response.data);
    } catch (error) {
      console.error('Erro ao buscar ciclo:', error);
    }
  };

  const fetchPDI = async () => {
    try {
      const response = await axios.get(`${API}/api/feedback-180/pdi/ciclo/${cicloId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setPdi(response.data);
    } catch (error) {
      console.error('Erro ao buscar PDI:', error);
    }
  };

  const handleGerarPDI = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${API}/api/feedback-180/gerar-pdi-automatico/${cicloId}`,
        {},
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      
      toast.success(`PDI gerado com ${response.data.total_acoes} ações sugeridas!`);
      fetchPDI();
    } catch (error) {
      toast.error('Erro ao gerar PDI');
    } finally {
      setLoading(false);
    }
  };

  const handleAtualizarStatus = async (acaoId, novoStatus) => {
    try {
      await axios.put(
        `${API}/api/feedback-180/pdi/${pdi.id}/acao/${acaoId}/status`,
        { novo_status: novoStatus },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          params: { novo_status: novoStatus }
        }
      );
      
      toast.success('Status atualizado!');
      fetchPDI();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const getPrioridadeColor = (prioridade) => {
    const colors = {
      alta: 'bg-red-100 text-red-700',
      media: 'bg-yellow-100 text-yellow-700',
      baixa: 'bg-green-100 text-green-700'
    };
    return colors[prioridade] || colors.media;
  };

  const getStatusIcon = (status) => {
    const icons = {
      pendente: <Clock className="h-4 w-4 text-gray-600" />,
      em_andamento: <Target className="h-4 w-4 text-blue-600" />,
      concluida: <CheckCircle2 className="h-4 w-4 text-green-600" />,
      cancelada: <AlertCircle className="h-4 w-4 text-red-600" />
    };
    return icons[status] || icons.pendente;
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <Button variant="outline" onClick={() => navigate(`/feedback/180/resultado/${cicloId}`)} className="mb-4">
        <ArrowLeft className="h-4 w-4 mr-2" />
        Voltar para Resultados
      </Button>

      <div className="space-y-6">
        {/* Header */}
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl flex items-center space-x-2">
              <Target className="h-6 w-6 text-purple-600" />
              <span>Plano de Desenvolvimento Individual (PDI)</span>
            </CardTitle>
            <CardDescription>
              {ciclo?.nome || 'Carregando...'} - Ações baseadas no seu feedback 180º
            </CardDescription>
          </CardHeader>
        </Card>

        {!pdi ? (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-12">
                <Sparkles className="h-12 w-12 text-purple-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  PDI não gerado ainda
                </h3>
                <p className="text-gray-600 mb-6">
                  Gere automaticamente seu Plano de Desenvolvimento baseado nos gaps identificados no feedback 180º
                </p>
                <Button
                  onClick={handleGerarPDI}
                  disabled={loading}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  {loading ? 'Gerando...' : 'Gerar PDI Automático'}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Resumo */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-sm text-gray-600">Total de Ações</p>
                    <p className="text-3xl font-bold text-purple-900">{pdi.acoes?.length || 0}</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-sm text-gray-600">Em Andamento</p>
                    <p className="text-3xl font-bold text-blue-900">
                      {pdi.acoes?.filter(a => a.status === 'em_andamento').length || 0}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-sm text-gray-600">Concluídas</p>
                    <p className="text-3xl font-bold text-green-900">
                      {pdi.acoes?.filter(a => a.status === 'concluida').length || 0}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Ações do PDI */}
            <div className="space-y-4">
              {pdi.acoes?.map((acao, index) => (
                <Card key={acao.id} className="border-l-4 border-purple-500">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <Badge className={getPrioridadeColor(acao.prioridade)}>
                            {acao.prioridade?.toUpperCase()}
                          </Badge>
                          {getStatusIcon(acao.status)}
                          <span className="text-sm text-gray-600 capitalize">
                            {acao.status?.replace('_', ' ')}
                          </span>
                        </div>
                        <CardTitle className="text-lg">
                          {index + 1}. {acao.competencia_nome}
                        </CardTitle>
                        <CardDescription className="mt-1">
                          Gap identificado: {acao.gap > 0 ? '+' : ''}{acao.gap.toFixed(1)}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Ação Sugerida */}
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2 flex items-center space-x-2">
                        <Target className="h-4 w-4 text-purple-600" />
                        <span>Ação Sugerida</span>
                      </h4>
                      <p className="text-sm text-gray-700 bg-purple-50 p-3 rounded">
                        {acao.acao_sugerida}
                      </p>
                    </div>

                    {/* Recursos Necessários */}
                    {acao.recursos_necessarios?.length > 0 && (
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2 flex items-center space-x-2">
                          <BookOpen className="h-4 w-4 text-blue-600" />
                          <span>Recursos Necessários</span>
                        </h4>
                        <ul className="space-y-1">
                          {acao.recursos_necessarios.map((recurso, idx) => (
                            <li key={idx} className="text-sm text-gray-700 flex items-center space-x-2">
                              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                              <span>{recurso}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Prazo */}
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <Clock className="h-4 w-4" />
                      <span>Prazo: {acao.prazo_dias} dias</span>
                    </div>

                    {/* Ações */}
                    <div className="flex space-x-2 pt-2">
                      {acao.status === 'pendente' && (
                        <Button
                          size="sm"
                          onClick={() => handleAtualizarStatus(acao.id, 'em_andamento')}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          Iniciar
                        </Button>
                      )}
                      
                      {acao.status === 'em_andamento' && (
                        <Button
                          size="sm"
                          onClick={() => handleAtualizarStatus(acao.id, 'concluida')}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          Concluir
                        </Button>
                      )}
                      
                      {acao.status !== 'cancelada' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleAtualizarStatus(acao.id, 'cancelada')}
                          className="text-red-600"
                        >
                          Cancelar
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Observações do Gestor */}
            {pdi.observacoes_gestor && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Observações do Gestor</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700">{pdi.observacoes_gestor}</p>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PDI180;
